import React, { useState, useEffect, type JSX } from "react";
import { MagnifyingGlassIcon, BookOpenIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import Tooltip from "./components/Tooltip";
import { useFeatureFlag } from "./hooks/useFeatureFlag";

interface ConversationStartersProps {
  onSelectStarter: (prompt: string, id: string) => void;
  disabled?: boolean;
}

interface Starter {
  id: string;
  label: string;
  followUpPrompt: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  enabled: boolean;
  exampleQueries?: string[];
  tooltip?: string;
}

const PAST_DEALS_STARTER: Starter = {
  id: "past-deals",
  label: "Find Deals & Ask Questions",
  followUpPrompt:
    "What type of deals are you interested in? You can search by company name, industry, deal type, or any other criteria.",
  icon: MagnifyingGlassIcon,
  enabled: true,
  exampleQueries: [
    "What telco companies have we looked at recently?",
    "Find CVC deals with preferred/structured equity",
    "I'm looking at Six Nations. Find relevant past deals",
    "What are typical IC concerns on recent tech deals?",
  ],
  tooltip:
    "Search and interrogate CVC's library of past deals and their documents. Private Equity: deals that reached PIR after 2011, with published IC papers and DD docs. Private Credit: deals with published IC papers. Other strategies coming soon",
};

const RESEARCH_DESK_STARTER: Starter = {
  id: "research-desk",
  label: "Search CVC Research",
  followUpPrompt:
    "What research are you looking for? You can ask about market overviews, sector analyses, company profiles, or any published CVC research.",
  icon: BookOpenIcon,
  enabled: false,
  exampleQueries: [
    "What research have we done on UK automotive recently?",
    "Find market overviews for European software",
    "Summarise our latest healthcare sector reports",
    "Show me any IC research on consumer goods",
  ],
  tooltip:
    "Query CVC's research library — IC papers, sector analyses, market overviews, company profiles. Results render as an inline table you can open, filter, and export.",
};

const EXPERT_FINDER_STARTER: Starter = {
  id: "expert-finder",
  label: "Find CVC Experts",
  followUpPrompt:
    "Who are you looking for? Describe the sector, geography, company or deal type, and I'll find CVC people with relevant deal experience.",
  icon: UserGroupIcon,
  enabled: false,
  exampleQueries: [
    "Who has worked on UK healthcare services deals?",
    "Who should I speak to about software carve-outs?",
    "Who knows the Nordic telco market?",
    "Who has led consumer deals in DACH?",
  ],
  tooltip:
    "Find CVC people by their PE deal-team experience. This is an expertise search, not an HR directory — it doesn't show availability, office or current role.",
};

interface StarterFlags {
  isResearchDeskEnabled: boolean;
  isExpertFinderEnabled: boolean;
}

/**
 * Hide starters gated behind an off feature flag rather than rendering them
 * as disabled placeholders — a flag-off tile with "Coming soon" leaks the
 * existence of the module before it's released.
 */
function visibleStarters(flags: StarterFlags): Starter[] {
  const all: Starter[] = [
    PAST_DEALS_STARTER,
    { ...RESEARCH_DESK_STARTER, enabled: flags.isResearchDeskEnabled },
    { ...EXPERT_FINDER_STARTER, enabled: flags.isExpertFinderEnabled },
  ];
  return all.filter((s) => s.enabled);
}

type IndexSetter = React.Dispatch<React.SetStateAction<Record<string, number>>>;

const ROTATION_INTERVAL_MS = 3000;

function startExampleRotation(id: string, count: number, setIndex: IndexSetter): NodeJS.Timeout {
  return setInterval(() => {
    setIndex((prev) => ({ ...prev, [id]: ((prev[id] || 0) + 1) % count }));
  }, ROTATION_INTERVAL_MS);
}

/** Rotate through example queries every 3 seconds for each card. */
function useRotatingExamples(starters: Starter[]): Record<string, number> {
  const [currentExampleIndex, setCurrentExampleIndex] = useState<Record<string, number>>({});

  useEffect(() => {
    const intervals: NodeJS.Timeout[] = [];

    starters.forEach((starter) => {
      const queries = starter.exampleQueries;
      if (queries && queries.length > 0) {
        intervals.push(startExampleRotation(starter.id, queries.length, setCurrentExampleIndex));
      }
    });

    return () => intervals.forEach((interval) => clearInterval(interval));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return currentExampleIndex;
}

function StarterExample({ starter, index }: { starter: Starter; index: number }): JSX.Element | null {
  if (!starter.enabled) {
    return <div className="text-xs text-gray-400 mt-0.5 min-h-10">Coming soon</div>;
  }

  if (!starter.exampleQueries || starter.exampleQueries.length === 0) {
    return null;
  }

  return (
    <div
      key={index}
      className="text-xs text-gray-500 mt-1 italic animate-fade-in min-h-10 flex items-start"
    >
      "{starter.exampleQueries[index]}"
    </div>
  );
}

function StarterCard({
  starter,
  isDisabled,
  exampleIndex,
  onSelect,
}: {
  starter: Starter;
  isDisabled: boolean;
  exampleIndex: number;
  onSelect: () => void;
}): JSX.Element {
  const Icon = starter.icon;

  return (
    <button
      onClick={onSelect}
      disabled={isDisabled}
      className={`relative flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-200 text-left min-h-[120px] w-full ${
        isDisabled
          ? "border-gray-100 bg-gray-50 cursor-not-allowed opacity-50"
          : "border-gray-200 bg-white hover:border-blue-500 hover:shadow-md cursor-pointer"
      }`}
    >
      {starter.enabled && (
        <span className="absolute top-2 right-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          BETA
        </span>
      )}
      <div
        className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
          isDisabled ? "bg-gray-200" : "bg-blue-100"
        }`}
      >
        <Icon className={`h-5 w-5 ${isDisabled ? "text-gray-400" : "text-blue-600"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className={`text-sm tablet:text-[18px] tablet:leading-[20px] font-medium ${
            isDisabled ? "text-gray-500" : "text-gray-900"
          }`}
        >
          {starter.label}
        </div>
        <StarterExample starter={starter} index={exampleIndex} />
      </div>
    </button>
  );
}

/**
 * Conversation starter pills component
 * Displays clickable options to help users start conversations
 */
export default function ConversationStarters_Component({
  onSelectStarter,
  disabled = false,
}: ConversationStartersProps): JSX.Element {
  const starters = visibleStarters({
    isResearchDeskEnabled: useFeatureFlag("inlineDeals"),
    isExpertFinderEnabled: useFeatureFlag("expertFinder"),
  });
  const currentExampleIndex = useRotatingExamples(starters);

  return (
    <div className="w-full max-w-2xl mx-auto px-4 py-2 sm:py-6">
      <div className="flex flex-col gap-3">
        {starters.map((starter) => {
          const isDisabled = disabled || !starter.enabled;
          return (
            <Tooltip key={starter.id} content={starter.tooltip || ""} disabled={isDisabled}>
              <StarterCard
                starter={starter}
                isDisabled={isDisabled}
                exampleIndex={currentExampleIndex[starter.id] || 0}
                onSelect={() => {
                  if (!isDisabled) {
                    onSelectStarter(starter.followUpPrompt, starter.id);
                  }
                }}
              />
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
